import jsonata from 'jsonata';
import { z } from 'zod';

export const DefaultGenericAllowanceFilterTemplate = z.object({
  templateKey: z.string(),
  name: z.string(),
  filterExpression: z.string(),
  dailyCounter: z.number(),
  globalCounter: z.number(),
  includedFiles: z.array(z.string()),
});
export type DefaultGenericAllowanceFilterTemplateType = z.infer<typeof DefaultGenericAllowanceFilterTemplate>;

export const DefaultGenericAllowanceTemplate = z.object({
  name: z.string(),
  description: z.string().default(''),
  enabled: z.boolean(),
  startDate: z.string(),
  endDate: z.string(),
  globalLimit: z.number().int().nonnegative(),
  dailyLimit: z.number().int().nonnegative(),
  filters: z.array(DefaultGenericAllowanceFilterTemplate),
});
export type DefaultGenericAllowanceTemplateType = z.infer<typeof DefaultGenericAllowanceTemplate>;

// raw shape of the S3 template document, validated for exact filter count, unique templateKeys and parseable JSONata.

export const DefaultGenericAllowanceConfigDocument = z
  .object({
    expectedFilterCount: z.number().int().positive(),
    allowance: DefaultGenericAllowanceTemplate,
  })
  .superRefine((doc, ctx) => {
    const { filters } = doc.allowance;

    if (filters.length !== doc.expectedFilterCount) {
      ctx.addIssue({
        code: 'custom',
        message: `Expected ${doc.expectedFilterCount} filters but found ${filters.length}`,
        path: ['allowance', 'filters'],
      });
    }

    const seenTemplateKeys = new Set<string>();
    filters.forEach((filter, index) => {
      if (seenTemplateKeys.has(filter.templateKey)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate templateKey "${filter.templateKey}"`,
          path: ['allowance', 'filters', index, 'templateKey'],
        });
      }
      seenTemplateKeys.add(filter.templateKey);

      try {
        jsonata(filter.filterExpression);
      } catch (err) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid JSONata expression for templateKey "${filter.templateKey}": ${(err as Error).message}`,
          path: ['allowance', 'filters', index, 'filterExpression'],
        });
      }
    });
  });
export type DefaultGenericAllowanceConfigDocumentType = z.infer<typeof DefaultGenericAllowanceConfigDocument>;

export const DefaultGenericAllowanceConfigResponse = z.discriminatedUnion('available', [
  z.object({
    available: z.literal(true),
    allowance: DefaultGenericAllowanceTemplate,
  }),
  z.object({
    available: z.literal(false),
  }),
]);
export type DefaultGenericAllowanceConfigResponseType = z.infer<typeof DefaultGenericAllowanceConfigResponse>;
